/**
 * Mental Map canvases, drawn with React Flow:
 *   MapEditor — one person's own area, with the legend they draw from
 *   MapView   — a read-only map, used for examples and the presentation stage
 *   RoomView  — everybody's maps on one canvas, each in its own area
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import {
  Background,
  BaseEdge,
  ConnectionMode,
  Controls,
  EdgeLabelRenderer,
  Handle,
  MarkerType,
  NodeResizer,
  NodeToolbar,
  Panel,
  Position,
  ReactFlow,
  ReactFlowProvider,
  getStraightPath,
  useEdgesState,
  useInternalNode,
  useNodesState,
  useReactFlow,
  useStore,
  type Connection,
  type Edge,
  type EdgeProps,
  type InternalNode,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { ArrowDownRight, Copy, Redo2, Trash2, Undo2 } from 'lucide-react';

import { Button } from '@/components/ui/Button';

import {
  AREA_HEIGHT,
  AREA_WIDTH,
  LABEL_MAX,
  LINK_LABEL_MAX,
  LINK_LEGEND,
  MAX_SHAPE,
  MIN_SHAPE,
  SIZE_LEGEND,
  TONE_LEGEND,
  boxOf,
  clampToArea,
  docBounds,
  keepsProportion,
  linkStyle,
  newMapId,
  pruneLinks,
  roomAreas,
  shapeBox,
  shapeCentre,
  shapeEdge,
  mapToneColor,
  type MapDoc,
  type MapLinkKind,
  type MapShape,
  type MapShapeKind,
  type MapSize,
  type MapTone,
} from './model';

const SHAPE_DRAG_TYPE = 'application/x-mental-map-shape';

type ShapeData = { kind: MapShapeKind; label: string; tone: MapTone; size: MapSize; editable: boolean; w?: number; h?: number };
type ShapeNode = Node<ShapeData, 'shape'>;
type AreaNode = Node<{ name?: string; ownerId?: string }, 'area'>;
type LinkData = { kind: MapLinkKind; editable: boolean; label?: string };
type LinkEdge = Edge<LinkData, 'link'>;

/**
 * "Go deeper" on a shape: open (or start) the more detailed model of that one
 * shape. Supplied by the shared-model screen; absent everywhere else.
 */
export interface DeeperActions {
  /** Does a deeper model of this shape already exist? */
  has: (shapeId: string) => boolean;
  open: (shapeId: string) => void;
  /** False at the most detailed level, where nothing new can be started. */
  canStart: boolean;
}

const DeeperContext = createContext<DeeperActions | null>(null);

/** In the whole-room view: open one person's map on its own. */
const OpenAreaContext = createContext<((ownerId: string) => void) | null>(null);

// ── Shape drawing ────────────────────────────────────────────────────────────

/** One legend shape as SVG, in a 100×100 box. Also used for the palette swatches. */
export function ShapeGlyph({ kind, color, width, height }: { kind: MapShapeKind; color: string; width: number; height: number }) {
  const common = {
    fill: color,
    fillOpacity: 0.12,
    stroke: color,
    strokeWidth: 2.5,
    vectorEffect: 'non-scaling-stroke' as const,
    style: { pointerEvents: 'visiblePainted' as const },
  };
  let shape: ReactNode;
  switch (kind) {
    case 'circle':
      shape = <circle cx="50" cy="50" r="46" {...common} />;
      break;
    case 'rectangle':
      shape = <rect x="4" y="16" width="92" height="68" {...common} />;
      break;
    case 'rounded':
      shape = <rect x="4" y="16" width="92" height="68" rx="20" {...common} />;
      break;
    case 'triangle':
      shape = <polygon points="50,6 96,92 4,92" {...common} />;
      break;
    case 'diamond':
      shape = <polygon points="50,4 96,50 50,96 4,50" {...common} />;
      break;
    case 'hexagon':
      shape = <polygon points="26,8 74,8 98,50 74,92 26,92 2,50" {...common} />;
      break;
    case 'ring':
      // A boundary: only the line itself is solid, so shapes inside stay reachable.
      shape = (
        <circle cx="50" cy="50" r="46" {...common} fill="none" strokeWidth={4} style={{ pointerEvents: 'stroke' }} />
      );
      break;
    case 'point':
      shape = <circle cx="50" cy="50" r="16" {...common} fillOpacity={1} />;
      break;
    case 'layer':
      shape = <rect x="1" y="3" width="98" height="94" {...common} />;
      break;
    case 'phase':
      shape = <polygon points="1,3 86,3 99,50 86,97 1,97 14,50" {...common} />;
      break;
    case 'note':
      // Words only. The invisible box is what you grab to move it.
      shape = <rect x="0" y="0" width="100" height="100" fill="transparent" stroke="none" style={{ pointerEvents: 'all' }} />;
      break;
  }
  return (
    <svg width={width} height={height} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" style={{ pointerEvents: 'none', display: 'block', flexShrink: 0 }}>
      {shape}
    </svg>
  );
}

const HANDLES: { id: string; position: Position }[] = [
  { id: 't', position: Position.Top },
  { id: 'r', position: Position.Right },
  { id: 'b', position: Position.Bottom },
  { id: 'l', position: Position.Left },
];

function labelPlacement(kind: MapShapeKind): string {
  if (kind === 'ring') return 'items-start pt-[14%]';
  if (kind === 'triangle') return 'items-end pb-[16%]';
  return 'items-center';
}

function ShapeNodeView({ id, data, selected }: NodeProps<ShapeNode>) {
  const flow = useReactFlow();
  const deeper = useContext(DeeperContext);
  const hasDeeper = deeper?.has(id) ?? false;
  const [editing, setEditing] = useState(false);
  // With several shapes selected, each one's own toolbar would pile up; the group gets one instead.
  const onlyOneSelected = useStore((state) => state.nodes.filter((n) => n.selected).length <= 1);
  const isNote = data.kind === 'note';
  const { w, h } = boxOf(data);
  const color = mapToneColor(data.tone);

  const duplicate = () => {
    const node = flow.getNode(id);
    if (!node) return;
    flow.setNodes((nodes) => [
      ...nodes.map((n) => ({ ...n, selected: false })),
      { ...node, id: newMapId('s'), position: { x: node.position.x + 28, y: node.position.y + 28 }, selected: true },
    ]);
  };

  return (
    <div
      className="relative"
      style={{ width: w, height: h, pointerEvents: 'none' }}
      onDoubleClick={() => data.editable && setEditing(true)}
    >
      {data.editable && (
        <NodeToolbar isVisible={selected && !editing && onlyOneSelected} position={Position.Top} offset={10}>
          <div className="flex items-center gap-2 rounded-lg border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1.5">
            {TONE_LEGEND.map((t) => (
              <Button
                key={t.tone}
                title={`${t.name} — ${t.meaning}`}
                aria-label={`${t.name}: ${t.meaning}`}
                onClick={() => flow.updateNodeData(id, { tone: t.tone })}
                variant="ghost" size="sm" className={`h-5 w-5 rounded-full border !p-0 ${data.tone === t.tone ? 'border-[var(--text-pri)]' : 'border-transparent'}`}
                style={{ backgroundColor: t.color }}
              />
            ))}
            <span className="mx-1 h-4 w-px bg-[var(--rule)]" />
            {SIZE_LEGEND.map((s) => (
              <Button
                key={s.size}
                title={s.name}
                onClick={() =>
                  // Back to a preset size: drop the hand-set size from both the shape and its frame.
                  flow.updateNode(id, (node) => ({
                    ...node,
                    width: undefined,
                    height: undefined,
                    data: { ...node.data, size: s.size, w: undefined, h: undefined },
                  }))
                }
                variant="ghost" size="sm" className={`!px-1 !py-0 font-mono uppercase ${data.size === s.size && data.w === undefined ? '!text-[var(--text-pri)]' : '!text-[var(--text-dim)]'}`}
              >
                {s.size}
              </Button>
            ))}
            {deeper && (hasDeeper || deeper.canStart) && (
              <>
                <span className="mx-1 h-4 w-px bg-[var(--rule)]" />
                <Button
                  variant="ghost"
                  size="sm"
                  className="!px-1 !py-0"
                  title={hasDeeper ? 'Open the deeper model of this shape' : 'Start a deeper model of this shape'}
                  onClick={() => deeper.open(id)}
                >
                  <ArrowDownRight className="h-4 w-4" />
                  {hasDeeper ? 'Open deeper' : 'Go deeper'}
                </Button>
              </>
            )}
            <span className="mx-1 h-4 w-px bg-[var(--rule)]" />
            <Button title="Make a copy of this shape" aria-label="Make a copy of this shape" variant="ghost" size="sm" className="!p-1" onClick={duplicate}>
              <Copy className="h-4 w-4" />
            </Button>
            <Button
              title="Remove this shape"
              aria-label="Remove this shape"
              onClick={() => void flow.deleteElements({ nodes: [{ id }] })}
              variant="ghost" size="sm" className="!p-1"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </NodeToolbar>
      )}

      <ShapeGlyph kind={data.kind} color={color} width={w} height={h} />
      {data.editable ? (
        // Drag any corner or edge to resize. Circles, rings and points keep their proportion.
        <NodeResizer
          isVisible={selected && !editing && onlyOneSelected}
          minWidth={MIN_SHAPE}
          minHeight={MIN_SHAPE}
          maxWidth={MAX_SHAPE}
          maxHeight={MAX_SHAPE}
          keepAspectRatio={keepsProportion(data.kind)}
          color="var(--text-ter)"
          handleStyle={{ pointerEvents: 'all', width: 10, height: 10, borderRadius: 2 }}
          lineStyle={{ pointerEvents: 'all' }}
          onResize={(_event, size) => flow.updateNodeData(id, { w: Math.round(size.width), h: Math.round(size.height) })}
        />
      ) : null}

      {!data.editable && selected && (
        // Picked out in a read-only map (choosing which shapes to bring across).
        <div className="pointer-events-none absolute -inset-1.5 rounded-md outline outline-2 outline-[var(--mustard)]" />
      )}

      {isNote && data.editable && (
        <div className="pointer-events-none absolute inset-0 rounded border border-dashed border-[var(--rule)]" />
      )}

      <div
        className={
          data.kind === 'point'
            ? 'absolute left-1/2 top-[72%] flex w-40 -translate-x-1/2 justify-center'
            : isNote
              ? 'absolute inset-0 flex items-start justify-start p-2'
              : `absolute inset-0 flex justify-center ${labelPlacement(data.kind)}`
        }
      >
        {editing ? (
          <textarea
            autoFocus
            defaultValue={data.label}
            maxLength={LABEL_MAX}
            rows={isNote ? 3 : 2}
            aria-label={isNote ? 'Note' : 'Shape label'}
            className={`nodrag nowheel resize-none rounded border border-[var(--rule-bright)] bg-[var(--bg-raised)] px-1 py-0.5 font-body text-sm text-[var(--text-pri)] outline-none ${
              isNote ? 'h-full w-full text-left' : 'w-[90%] text-center'
            }`}
            style={{ pointerEvents: 'auto' }}
            onFocus={(e) => e.target.select()}
            onBlur={(e) => {
              flow.updateNodeData(id, { label: e.target.value.trim() });
              setEditing(false);
            }}
            onKeyDown={(e) => {
              // Enter finishes; Shift+Enter starts a new line.
              if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Escape') {
                e.preventDefault();
                (e.target as HTMLTextAreaElement).blur();
              }
              e.stopPropagation();
            }}
          />
        ) : (
          <span
            className={`whitespace-pre-wrap break-words font-body text-sm leading-tight text-[var(--text-pri)] ${
              isNote ? 'w-full text-left' : 'max-w-[86%] text-center'
            }`}
            style={{ pointerEvents: 'auto' }}
          >
            {data.label ||
              (data.editable ? (
                <span className="text-[var(--text-dim)]">{isNote ? 'Double-click to write' : 'Double-click to name'}</span>
              ) : null)}
          </span>
        )}
      </div>

      {hasDeeper && (
        <Button
          variant="cta"
          size="sm"
          className="nodrag absolute -bottom-2 -right-2 !gap-0.5 rounded-full !px-1.5 !py-0.5"
          style={{ pointerEvents: 'auto' }}
          title="This shape has a deeper model — open it"
          aria-label="Open the deeper model of this shape"
          onClick={(e) => {
            e.stopPropagation();
            deeper?.open(id);
          }}
        >
          <ArrowDownRight className="h-3 w-3" />
        </Button>
      )}

      {HANDLES.map((h) => (
        <Handle
          key={h.id}
          id={h.id}
          type="source"
          position={h.position}
          isConnectable={data.editable}
          style={{
            pointerEvents: data.editable ? 'auto' : 'none',
            opacity: data.editable && selected ? 1 : 0,
            width: 10,
            height: 10,
            background: color,
          }}
        />
      ))}
    </div>
  );
}

function AreaNodeView({ data, width, height }: NodeProps<AreaNode>) {
  const openArea = useContext(OpenAreaContext);
  return (
    <div
      className="rounded-xl border border-dashed border-[var(--rule-bright)]"
      style={{ width: width ?? AREA_WIDTH, height: height ?? AREA_HEIGHT, pointerEvents: 'none' }}
    >
      {data.name && openArea && data.ownerId ? (
        <Button
          variant="secondary"
          className="nodrag nopan absolute -top-24 left-0 !px-6 !py-3 !text-4xl"
          style={{ pointerEvents: 'auto' }}
          title={`Show only ${data.name}'s map`}
          onClick={() => openArea(data.ownerId!)}
        >
          {data.name}
        </Button>
      ) : (
        data.name && (
          <div className="absolute -top-16 left-1 font-heading text-4xl font-semibold text-[var(--text-sec)]">{data.name}</div>
        )
      )}
    </div>
  );
}

/** A node as the model sees it: a shape at a place. */
function placed(node: InternalNode) {
  const data = node.data as ShapeData;
  return { kind: data.kind, size: data.size, w: data.w, h: data.h, x: node.internals.positionAbsolute.x, y: node.internals.positionAbsolute.y };
}

function LinkEdgeView({ id, source, target, data, selected, markerEnd }: EdgeProps<LinkEdge>) {
  const flow = useReactFlow();
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  const kind = data?.kind ?? 'leads_to';
  const style = linkStyle(kind);
  if (!sourceNode || !targetNode) return null;
  const from = shapeEdge(placed(sourceNode), shapeCentre(placed(targetNode)));
  const to = shapeEdge(placed(targetNode), shapeCentre(placed(sourceNode)));
  const [path, labelX, labelY] = getStraightPath({ sourceX: from.x, sourceY: from.y, targetX: to.x, targetY: to.y });

  const setKind = (next: MapLinkKind) =>
    flow.setEdges((edges) => edges.map((e) => (e.id === id ? withLinkKind(e as LinkEdge, next) : e)));
  const setLabel = (label: string) => flow.updateEdgeData(id, { label });
  const words = data?.label?.trim();
  const showPicker = selected && data?.editable;

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        markerEnd={markerEnd}
        style={{ stroke: style.color, strokeWidth: selected ? 3 : 2, strokeDasharray: style.dashed ? '7 6' : undefined }}
      />
      {words && !showPicker && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan absolute rounded bg-[var(--bg)] px-1.5 py-0.5 font-body text-xs"
            style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`, color: style.color }}
          >
            {words}
          </div>
        </EdgeLabelRenderer>
      )}
      {showPicker && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan absolute flex items-center gap-1 rounded-lg border border-[var(--rule)] bg-[var(--bg-raised)] px-1.5 py-1"
            style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`, pointerEvents: 'all' }}
          >
            <input
              value={data?.label ?? ''}
              maxLength={LINK_LABEL_MAX}
              placeholder="What this line says"
              aria-label="What this line says"
              className="w-40 rounded border border-[var(--rule-bright)] bg-[var(--bg-raised)] px-1.5 py-0.5 font-body text-xs text-[var(--text-pri)] outline-none"
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
            />
            {LINK_LEGEND.map((l) => (
              <Button
                key={l.kind}
                title={l.meaning}
                onClick={() => setKind(l.kind)}
                variant="ghost" size="sm" className={`rounded !px-1.5 !py-0.5 font-body ${l.kind === kind ? '!bg-[var(--text-pri)] !text-[var(--bg)]' : ''}`}
              >
                {l.name}
              </Button>
            ))}
            <Button
              title="Remove this line"
              aria-label="Remove this line"
              onClick={() => void flow.deleteElements({ edges: [{ id }] })}
              variant="ghost" size="sm" className="!p-1"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const nodeTypes = { shape: ShapeNodeView, area: AreaNodeView };
const edgeTypes = { link: LinkEdgeView };

// ── Map document ⇄ React Flow ────────────────────────────────────────────────

function withLinkKind(edge: LinkEdge, kind: MapLinkKind): LinkEdge {
  const style = linkStyle(kind);
  return {
    ...edge,
    data: { ...edge.data, kind, editable: edge.data?.editable ?? false },
    markerEnd: style.arrow ? { type: MarkerType.ArrowClosed, color: style.color, width: 18, height: 18 } : undefined,
  };
}

interface FlowOptions {
  editable: boolean;
  prefix?: string;
  offsetX?: number;
  offsetY?: number;
  areaName?: string;
  areaOwnerId?: string;
  areaWidth?: number;
  /** Move the shapes inside their area (the area itself stays put). */
  shiftX?: number;
  shiftY?: number;
  areaHeight?: number;
  /** Draw the owner's area outline. Off for a map shown on its own, so the view fits the shapes. */
  showArea?: boolean;
}

function docToFlow(doc: MapDoc, opts: FlowOptions): { nodes: Node[]; edges: LinkEdge[] } {
  const prefix = opts.prefix ?? '';
  const ox = opts.offsetX ?? 0;
  const oy = opts.offsetY ?? 0;
  const area: AreaNode = {
    id: `${prefix}__area`,
    type: 'area',
    position: { x: ox, y: oy },
    data: { name: opts.areaName, ownerId: opts.areaOwnerId },
    width: opts.areaWidth ?? AREA_WIDTH,
    height: opts.areaHeight ?? AREA_HEIGHT,
    selectable: false,
    draggable: false,
    deletable: false,
    focusable: false,
    zIndex: -10,
    style: { pointerEvents: 'none' },
  };
  const shapes: ShapeNode[] = doc.shapes.map((s) => ({
    id: `${prefix}${s.id}`,
    type: 'shape',
    position: { x: ox + s.x + (opts.shiftX ?? 0), y: oy + s.y + (opts.shiftY ?? 0) },
    data: { kind: s.kind, label: s.label, tone: s.tone, size: s.size, editable: opts.editable, w: s.w, h: s.h },
    // Boundaries and layers sit behind, so other shapes can be placed on them.
    zIndex: s.kind === 'ring' || s.kind === 'layer' ? 0 : 1,
    style: { pointerEvents: 'none' },
  }));
  const edges: LinkEdge[] = doc.links.map((l) =>
    withLinkKind(
      {
        id: `${prefix}${l.id}`,
        type: 'link',
        source: `${prefix}${l.from}`,
        target: `${prefix}${l.to}`,
        data: { kind: l.kind, editable: opts.editable, label: l.label },
        zIndex: 2,
      },
      l.kind,
    ),
  );
  return { nodes: opts.showArea === false ? shapes : [area, ...shapes], edges };
}

function flowToDoc(nodes: Node[], edges: Edge[]): MapDoc {
  const shapes: MapShape[] = nodes
    .filter((n): n is ShapeNode => n.type === 'shape')
    .map((n) =>
      clampToArea({
        id: n.id,
        kind: n.data.kind,
        label: n.data.label,
        tone: n.data.tone,
        size: n.data.size,
        x: Math.round(n.position.x),
        y: Math.round(n.position.y),
        ...(n.data.w !== undefined && n.data.h !== undefined ? { w: n.data.w, h: n.data.h } : {}),
      }),
    );
  return pruneLinks({
    shapes,
    links: edges.map((e) => ({
      id: e.id,
      from: e.source,
      to: e.target,
      kind: (e.data as LinkData | undefined)?.kind ?? 'leads_to',
      ...((e.data as LinkData | undefined)?.label?.trim() ? { label: (e.data as LinkData).label!.trim() } : {}),
    })),
  });
}

const FLOW_CHROME = {
  nodeTypes,
  edgeTypes,
  proOptions: { hideAttribution: true },
  // Every shape has one kind of dot, so a line may start and end on any of them.
  // Without this React Flow finds no end for a line and draws nothing.
  connectionMode: ConnectionMode.Loose,
  minZoom: 0.05,
  maxZoom: 4,
} as const;

// ── Editor ───────────────────────────────────────────────────────────────────

interface MapEditorProps {
  /** The map to start from. Remount (change `key`) to load a different one. */
  initialDoc: MapDoc;
  onChange: (doc: MapDoc) => void;
  /** Rendered beside the canvas, inside the same React Flow context. */
  legend: (api: { addShape: (kind: MapShapeKind) => void; dragType: string }) => ReactNode;
  deeper?: DeeperActions;
}

function MapEditorInner({ initialDoc, onChange, legend }: MapEditorProps) {
  const flow = useReactFlow();
  const wrapper = useRef<HTMLDivElement>(null);
  const initial = useMemo(() => docToFlow(initialDoc, { editable: true, showArea: false }), [initialDoc]);
  const startsEmpty = initialDoc.shapes.length === 0;
  const [nodes, setNodes, onNodesChange] = useNodesState(initial.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<LinkEdge>(initial.edges);

  // Undo history: the map as text, one entry per pause in editing (a drag is one
  // step, not one per pixel). Selection is not part of the map, so it is not a step.
  const history = useRef<{ stack: string[]; index: number }>({
    stack: [JSON.stringify(flowToDoc(initial.nodes, initial.edges))],
    index: 0,
  });
  const latestJson = useRef(history.current.stack[0]);
  const historyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const commitHistory = useCallback(() => {
    if (historyTimer.current) clearTimeout(historyTimer.current);
    historyTimer.current = null;
    const h = history.current;
    if (latestJson.current === h.stack[h.index]) return;
    h.stack = [...h.stack.slice(0, h.index + 1), latestJson.current].slice(-100);
    h.index = h.stack.length - 1;
    setCanUndo(h.index > 0);
    setCanRedo(false);
  }, []);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    const doc = flowToDoc(nodes, edges);
    onChangeRef.current(doc);
    latestJson.current = JSON.stringify(doc);
    if (latestJson.current === history.current.stack[history.current.index]) return;
    if (historyTimer.current) clearTimeout(historyTimer.current);
    historyTimer.current = setTimeout(commitHistory, 350);
  }, [nodes, edges, commitHistory]);

  useEffect(
    () => () => {
      if (historyTimer.current) clearTimeout(historyTimer.current);
    },
    [],
  );

  const stepHistory = useCallback(
    (delta: -1 | 1) => {
      commitHistory();
      const h = history.current;
      const next = h.index + delta;
      if (next < 0 || next >= h.stack.length) return;
      h.index = next;
      latestJson.current = h.stack[next];
      const restored = docToFlow(JSON.parse(h.stack[next]) as MapDoc, { editable: true, showArea: false });
      setNodes(restored.nodes);
      setEdges(restored.edges);
      setCanUndo(next > 0);
      setCanRedo(next < h.stack.length - 1);
    },
    [commitHistory, setNodes, setEdges],
  );

  const selectedCount = nodes.filter((n) => n.selected).length;

  /** Copy every selected shape, and the lines between them, a little down and to the right. */
  const duplicateSelected = useCallback(() => {
    const chosen = nodes.filter((n) => n.selected && n.type === 'shape');
    if (chosen.length === 0) return;
    const idFor = new Map(chosen.map((n) => [n.id, newMapId('s')]));
    const copies = chosen.map((n) => ({
      ...n,
      id: idFor.get(n.id)!,
      position: { x: n.position.x + 28, y: n.position.y + 28 },
      selected: true,
    }));
    const lineCopies = edges
      .filter((e) => idFor.has(e.source) && idFor.has(e.target))
      .map((e) => ({ ...e, id: newMapId('l'), source: idFor.get(e.source)!, target: idFor.get(e.target)!, selected: false }));
    setNodes((current) => [...current.map((n) => ({ ...n, selected: false })), ...copies]);
    setEdges((current) => [...current, ...lineCopies]);
  }, [nodes, edges, setNodes, setEdges]);

  const deleteSelected = useCallback(() => {
    void flow.deleteElements({ nodes: nodes.filter((n) => n.selected).map((n) => ({ id: n.id })) });
  }, [flow, nodes]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (!(event.metaKey || event.ctrlKey)) return;
      const key = event.key.toLowerCase();
      if (key === 'z') {
        event.preventDefault();
        stepHistory(event.shiftKey ? 1 : -1);
      } else if (key === 'y') {
        event.preventDefault();
        stepHistory(1);
      } else if (key === 'd') {
        event.preventDefault();
        duplicateSelected();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stepHistory, duplicateSelected]);

  const placeShape = useCallback(
    (kind: MapShapeKind, at: { x: number; y: number }) => {
      const size: MapSize = kind === 'ring' ? 'l' : kind === 'point' ? 's' : 'm';
      const { w, h } = shapeBox(kind, size);
      const shape = clampToArea({ id: newMapId('s'), kind, label: '', tone: 'neutral', size, x: at.x - w / 2, y: at.y - h / 2 });
      const [node] = docToFlow({ shapes: [shape], links: [] }, { editable: true, showArea: false }).nodes;
      setNodes((current) => [...current.map((n) => ({ ...n, selected: false })), { ...node, selected: true }]);
    },
    [setNodes],
  );

  const addShape = useCallback(
    (kind: MapShapeKind) => {
      const box = wrapper.current?.getBoundingClientRect();
      const centre = box
        ? flow.screenToFlowPosition({ x: box.left + box.width / 2, y: box.top + box.height / 2 })
        : { x: AREA_WIDTH / 2, y: AREA_HEIGHT / 2 };
      // Nudge each new shape so several added in a row do not stack exactly.
      const nudge = (nodes.length % 6) * 18;
      placeShape(kind, { x: centre.x + nudge, y: centre.y + nudge });
    },
    [flow, nodes.length, placeShape],
  );

  const onDrop = useCallback(
    (event: DragEvent) => {
      const kind = event.dataTransfer.getData(SHAPE_DRAG_TYPE) as MapShapeKind;
      if (!kind) return;
      event.preventDefault();
      placeShape(kind, flow.screenToFlowPosition({ x: event.clientX, y: event.clientY }));
    },
    [flow, placeShape],
  );

  const onConnect = useCallback(
    (c: Connection) => {
      if (!c.source || !c.target || c.source === c.target) return;
      setEdges((current) => [
        ...current.map((e) => ({ ...e, selected: false })),
        {
          ...withLinkKind(
            { id: newMapId('l'), type: 'link', source: c.source, target: c.target, data: { kind: 'leads_to', editable: true }, zIndex: 2 },
            'leads_to',
          ),
          selected: true,
        },
      ]);
    },
    [setEdges],
  );

  return (
    <div className="flex h-full min-h-0 w-full">
      {legend({ addShape, dragType: SHAPE_DRAG_TYPE })}
      <div
        ref={wrapper}
        className="relative min-w-0 flex-1 bg-[var(--bg)]"
        onDrop={onDrop}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
        }}
      >
        <ReactFlow
          {...FLOW_CHROME}
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeExtent={[
            [0, 0],
            [AREA_WIDTH, AREA_HEIGHT],
          ]}
          translateExtent={[
            [-800, -600],
            [AREA_WIDTH + 800, AREA_HEIGHT + 600],
          ]}
          deleteKeyCode={['Backspace', 'Delete']}
          // A map with shapes opens fitted to them, never zoomed past life size.
          // An empty one opens in the middle of the space, with room every way.
          fitView={!startsEmpty}
          fitViewOptions={{ padding: 0.25, maxZoom: 1 }}
          onInit={(instance) => {
            if (startsEmpty) void instance.setCenter(AREA_WIDTH / 2, AREA_HEIGHT / 2, { zoom: 1 });
          }}
        >
          <Background gap={32} />
          <Controls showInteractive={false} position="bottom-right" />
          <Panel position="top-left">
            <div className="flex items-center gap-1 rounded-lg border border-[var(--rule)] bg-[var(--bg-raised)] px-1.5 py-1">
              <Button variant="ghost" size="sm" className="!p-1" disabled={!canUndo} title="Undo (Ctrl or ⌘ + Z)" aria-label="Undo" onClick={() => stepHistory(-1)}>
                <Undo2 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" className="!p-1" disabled={!canRedo} title="Redo (Shift + Ctrl or ⌘ + Z)" aria-label="Redo" onClick={() => stepHistory(1)}>
                <Redo2 className="h-4 w-4" />
              </Button>
              {selectedCount > 1 && (
                <>
                  <span className="mx-1 h-4 w-px bg-[var(--rule)]" />
                  <span className="font-body text-xs text-[var(--text-sec)]">{selectedCount} selected</span>
                  <Button variant="ghost" size="sm" className="!p-1" title="Copy the selected shapes (Ctrl or ⌘ + D)" aria-label="Copy the selected shapes" onClick={duplicateSelected}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="!p-1" title="Remove the selected shapes" aria-label="Remove the selected shapes" onClick={deleteSelected}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </Panel>
        </ReactFlow>
      </div>
    </div>
  );
}

export function MapEditor(props: MapEditorProps) {
  return (
    <DeeperContext.Provider value={props.deeper ?? null}>
      <ReactFlowProvider>
        <MapEditorInner {...props} />
      </ReactFlowProvider>
    </DeeperContext.Provider>
  );
}

// ── Read-only views ──────────────────────────────────────────────────────────

/** Re-fit the view whenever the tile it sits in changes size (stage layouts do this). */
function RefitOnResize({ target }: { target: React.RefObject<HTMLDivElement | null> }) {
  const flow = useReactFlow();
  useEffect(() => {
    const el = target.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      void flow.fitView({ padding: 0.12 });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [flow, target]);
  return null;
}

function StaticFlow({ nodes, edges, interactive }: { nodes: Node[]; edges: Edge[]; interactive: boolean }) {
  const wrapper = useRef<HTMLDivElement>(null);
  // React Flow owns the shapes here (it has to measure them before it will draw
  // the lines between them), so a changed picture is shown by mounting a fresh
  // flow. "Changed" means its content: a live room hands over new arrays on
  // every poll, and remounting for those would lose the viewer's pan and zoom.
  const shown = useRef<{ signature: string; key: number }>({ signature: '', key: 0 });
  const signature = useMemo(
    () =>
      JSON.stringify([
        nodes.map((n) => [n.id, n.position.x, n.position.y, n.data]),
        edges.map((e) => [e.id, e.source, e.target, e.data]),
      ]),
    [nodes, edges],
  );
  if (shown.current.signature !== signature) {
    shown.current = { signature, key: shown.current.key + 1 };
  }
  const flowKey = shown.current.key;
  return (
    <div ref={wrapper} className="h-full w-full bg-[var(--bg)]">
      <ReactFlowProvider key={flowKey}>
        <ReactFlow
          {...FLOW_CHROME}
          defaultNodes={nodes}
          defaultEdges={edges}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag={interactive}
          zoomOnScroll={interactive}
          zoomOnPinch={interactive}
          zoomOnDoubleClick={false}
          preventScrolling={interactive}
          fitView
          fitViewOptions={{ padding: 0.12 }}
        >
          <RefitOnResize target={wrapper} />
          {interactive && <Controls showInteractive={false} position="bottom-right" />}
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
}

/**
 * A read-only map you can pick shapes out of: click one, Shift-click to add,
 * or drag across several. Reports the picked shapes' ids.
 */
function MapPickerInner({ doc, onPick }: { doc: MapDoc; onPick: (shapeIds: string[]) => void }) {
  const initial = useMemo(() => docToFlow(doc, { editable: false, showArea: false }), [doc]);
  const [nodes, , onNodesChange] = useNodesState(initial.nodes);
  const [edges, , onEdgesChange] = useEdgesState<LinkEdge>(initial.edges);
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;
  const picked = nodes.filter((n) => n.selected).map((n) => n.id).join('|');
  useEffect(() => {
    onPickRef.current(picked ? picked.split('|') : []);
  }, [picked]);
  return (
    <ReactFlow
      {...FLOW_CHROME}
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodesDraggable={false}
      nodesConnectable={false}
      edgesFocusable={false}
      // Dragging picks; the wheel (or two fingers) moves around.
      selectionOnDrag
      panOnDrag={[1, 2]}
      panOnScroll
      multiSelectionKeyCode={['Shift', 'Meta', 'Control']}
      deleteKeyCode={null}
      fitView
      fitViewOptions={{ padding: 0.15 }}
    >
      <Controls showInteractive={false} position="bottom-right" />
    </ReactFlow>
  );
}

export function MapPicker(props: { doc: MapDoc; onPick: (shapeIds: string[]) => void }) {
  return (
    <div className="h-full w-full bg-[var(--bg)]">
      <ReactFlowProvider>
        <MapPickerInner {...props} />
      </ReactFlowProvider>
    </div>
  );
}

/** One map, read-only. `interactive` allows pan and zoom (the stage); thumbnails turn it off. */
export function MapView({ doc, interactive = false, deeper }: { doc: MapDoc; interactive?: boolean; deeper?: DeeperActions }) {
  const { nodes, edges } = useMemo(() => docToFlow(doc, { editable: false, showArea: false }), [doc]);
  return (
    <DeeperContext.Provider value={deeper ?? null}>
      <StaticFlow nodes={nodes} edges={edges} interactive={interactive} />
    </DeeperContext.Provider>
  );
}

/** Everybody on one canvas, each map in its own named area. */
export function RoomView({ maps, onOpen }: { maps: { id: string; name: string; doc: MapDoc }[]; onOpen?: (id: string) => void }) {
  const { nodes, edges } = useMemo(() => {
    // Every cell is as large as the largest map, and each map sits centred in its cell.
    const pad = 80;
    const bounds = maps.map((m) => docBounds(m.doc));
    const cellW = Math.max(600, ...bounds.map((b) => b.w)) + pad * 2;
    const cellH = Math.max(400, ...bounds.map((b) => b.h)) + pad * 2;
    const areas = roomAreas(maps.map((m) => m.id), cellW, cellH);
    const allNodes: Node[] = [];
    const allEdges: Edge[] = [];
    maps.forEach((m, i) => {
      const part = docToFlow(m.doc, {
        editable: false,
        prefix: `${m.id}:`,
        offsetX: areas[i].x,
        offsetY: areas[i].y,
        areaName: m.name,
        areaOwnerId: m.id,
        areaWidth: cellW,
        areaHeight: cellH,
        shiftX: (cellW - bounds[i].w) / 2 - bounds[i].x,
        shiftY: (cellH - bounds[i].h) / 2 - bounds[i].y,
      });
      allNodes.push(...part.nodes);
      allEdges.push(...part.edges);
    });
    return { nodes: allNodes, edges: allEdges };
  }, [maps]);
  return (
    <OpenAreaContext.Provider value={onOpen ?? null}>
      <StaticFlow nodes={nodes} edges={edges} interactive />
    </OpenAreaContext.Provider>
  );
}
